import {
  useState,
  useRef,
  useEffect,
  useCallback,
  KeyboardEvent,
  ChangeEvent,
  forwardRef,
  useImperativeHandle,
  DragEvent,
} from 'react';

const ACCEPTED_EXTS = '.pdf,.png,.jpg,.jpeg,.docx,.xlsx,.csv,.md,.txt';

// Per-file attachment limits. Must stay in lockstep with the server's
// chatUpload multer config in apps/api/src/routes/sessions.ts:184 —
// otherwise a file that passes the client check still fails on the
// server. The previous behaviour silently uploaded oversized files and
// surfaced an opaque "Failed to fetch" when the reverse proxy
// (`client_max_body_size` < attachment size) aborted the request
// mid-stream; pre-flight rejection here keeps the failure local and
// the message informative.
const MAX_CHAT_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const MAX_CHAT_FILE_SIZE_LABEL = '10 MB';
const MAX_CHAT_ATTACHMENTS     = 5;

interface ChatInputProps {
  /** Controlled value — managed by parent so the draft survives re-renders. */
  value: string;
  onChange: (value: string) => void;
  /** Session ID used to key the sessionStorage draft. Restore fires once when it becomes available. */
  sessionId?: string;
  onSend: (content: string, files?: File[]) => void;
  disabled: boolean;
  onRetry?: () => void;
  /** Callback to stop/cancel the current streaming response */
  onStop?: () => void;
}

export interface ChatInputHandle {
  focus: () => void;
}

const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(function ChatInput(
  { value, onChange, sessionId, onSend, disabled, onRetry, onStop },
  ref,
) {
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  // Inline attachment error — set when addFiles rejects an oversized
  // file. Cleared on the next addFiles call or on send. Rendered
  // immediately below the attachment chip row so the user sees what
  // was dropped and why it didn't take.
  const [attachError, setAttachError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({
    focus: () => textareaRef.current?.focus(),
  }));

  // Restore persisted draft when the session ID first becomes available
  useEffect(() => {
    if (!sessionId) return;
    const saved = sessionStorage.getItem(`chat-draft-${sessionId}`);
    if (saved) onChange(saved);
    // onChange is the useState setter from the parent — stable across renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const adjustHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    // Cap at 5 lines (~120px)
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, []);

  // Sync textarea height whenever value changes (covers external restoration from sessionStorage)
  useEffect(() => {
    adjustHeight();
  }, [value, adjustHeight]);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    onChange(text);
    if (sessionId) {
      if (text) {
        sessionStorage.setItem(`chat-draft-${sessionId}`, text);
      } else {
        sessionStorage.removeItem(`chat-draft-${sessionId}`);
      }
    }
    adjustHeight();
  };

  const addFiles = (incoming: FileList | File[]) => {
    const arr = Array.from(incoming);
    const accepted: File[] = [];
    const rejected: string[] = [];
    for (const file of arr) {
      if (file.size > MAX_CHAT_FILE_SIZE_BYTES) {
        rejected.push(`"${file.name}" (${(file.size / (1024 * 1024)).toFixed(1)} MB)`);
      } else {
        accepted.push(file);
      }
    }
    setAttachError(
      rejected.length === 0
        ? null
        : `${rejected.join(', ')} ${rejected.length === 1 ? 'is' : 'are'} too large — max ${MAX_CHAT_FILE_SIZE_LABEL} per file.`,
    );
    setAttachedFiles(prev => {
      const combined = [...prev, ...accepted];
      return combined.slice(0, MAX_CHAT_ATTACHMENTS);
    });
  };

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) addFiles(e.target.files);
    // Reset input so the same file can be re-selected after removal
    e.target.value = '';
  };

  const removeFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
    // Clearing the chip row implies the user has dismissed any prior
    // rejection too — keeping the error stale alongside an empty list
    // would be confusing.
    setAttachError(null);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files) addFiles(e.dataTransfer.files);
  };

  const handleSend = () => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed, attachedFiles.length > 0 ? attachedFiles : undefined);
    onChange('');
    if (sessionId) sessionStorage.removeItem(`chat-draft-${sessionId}`);
    setAttachedFiles([]);
    setAttachError(null);
    // Reset height
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      style={{
        borderTop: '1px solid var(--border)',
        background: dragOver ? 'rgba(124,58,237,0.04)' : 'var(--surface)',
      }}
      className="px-4 py-3 transition-colors flex-shrink-0"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {onRetry && (
        <div className="flex justify-center mb-2">
          <button
            onClick={onRetry}
            style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--primary)' }}
            className="hover:underline"
          >
            ↩ Retry last message
          </button>
        </div>
      )}
      {disabled && !onStop && (
        <div className="flex items-center gap-2 mb-2 text-xs text-gray-400">
          <span className="inline-block w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: 'var(--primary)' }} />
          BluePrint is thinking…
        </div>
      )}
      {onStop && (
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-block w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: 'var(--primary)' }} />
          <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Agent is responding…</span>
          <button
            onClick={onStop}
            className="text-xs font-medium px-2.5 py-1 rounded-full transition-colors"
            style={{
              color: 'var(--error, #ef4444)',
              background: 'rgba(239,68,68,0.08)',
              border: '1px solid rgba(239,68,68,0.2)',
              cursor: 'pointer',
              fontFamily: 'var(--font-body)',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.15)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.08)'; }}
          >
            Stop generating
          </button>
        </div>
      )}

      {/* Attached file chips */}
      {attachedFiles.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {attachedFiles.map((file, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium max-w-[180px]"
              style={{ background: 'rgba(124,58,237,0.10)', color: 'var(--primary)' }}
            >
              <span className="truncate">{file.name}</span>
              <button
                onClick={() => removeFile(i)}
                className="flex-shrink-0 hover:text-red-500 transition-colors leading-none"
                aria-label={`Remove ${file.name}`}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Attachment validation error (oversized files). Rendered inline
          below the chip row so the user sees exactly which dropped file
          was rejected and why. */}
      {attachError && (
        <div
          data-testid="chat-attach-error"
          className="text-xs mb-2"
          role="alert"
          style={{ color: 'rgb(180, 83, 9)' }}
        >
          {attachError}
        </div>
      )}

      <div className="flex items-end gap-2">
        {/* Attachment button */}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || attachedFiles.length >= MAX_CHAT_ATTACHMENTS}
          className="flex-shrink-0 p-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ color: 'var(--text-secondary)' }}
          onMouseEnter={e => (e.currentTarget.style.color = 'var(--primary)')}
          onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-secondary)')}
          aria-label="Attach file"
          title="Attach file (PDF, image, Word, Excel, CSV, text)"
        >
          {/* Paperclip icon */}
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
          </svg>
        </button>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPTED_EXTS}
          className="hidden"
          onChange={handleFileInputChange}
        />

        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder="Type your message… (Enter to send, Shift+Enter for newline)"
          rows={1}
          className="flex-1 resize-none rounded-xl px-4 py-2.5 text-sm focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed leading-relaxed overflow-y-auto"
          style={{
            background: 'var(--surface-alt)',
            border: '1px solid var(--border)',
            color: 'var(--text)',
            fontFamily: 'var(--font-body)',
            minHeight: '42px',
            maxHeight: '120px',
          }}
          onFocus={e => (e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.15)')}
          onBlur={e => (e.currentTarget.style.boxShadow = 'none')}
        />
        <button
          onClick={handleSend}
          disabled={disabled || !value.trim()}
          className="flex-shrink-0 px-4 py-2.5 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: 'var(--primary)', fontFamily: 'var(--font-body)' }}
        >
          Send
        </button>
      </div>
    </div>
  );
});

export default ChatInput;
